import React, { useState, useEffect } from 'react';
import { 
  User, Mail, Calendar, MapPin, Plane, DollarSign, 
  LogOut, Settings, Clock, ArrowRight, CheckCircle,
  History, Search, Trash2, Edit2, Save, X, Star, CreditCard
} from 'lucide-react';
import { supabase, signOut, getUserTrips } from '../supabaseClient';
import Footer from '../components/Footer';
import PreferencesTab from '../components/PreferencesTab';
import SubscriptionTab from '../components/SubscriptionTab';
import './AccountPage.css';

function AccountPage({ user, onSignOut }) {
  const [trips, setTrips] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('trips'); // 'trips', 'history', 'preferences', or 'profile'
  const [editingProfile, setEditingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    fullName: '',
    phone: '',
    dateOfBirth: '',
    address: '',
  });

  useEffect(() => {
    // Check if user is logged in
    if (!user) {
      window.location.href = '/';
      return;
    }
    
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    if (!user) return;
    
    setLoading(true);
    
    // Load trips
    const { data: tripsData, error: tripsError } = await getUserTrips(user.id);
    if (!tripsError && tripsData) {
      setTrips(tripsData);
    }
    
    // Load search history from localStorage (or from database if implemented)
    const storedHistory = localStorage.getItem(`searchHistory_${user.id}`);
    if (storedHistory) {
      setSearchHistory(JSON.parse(storedHistory));
    }
    
    // Load profile data
    setProfileData({
      fullName: user?.user_metadata?.full_name || '',
      phone: user?.user_metadata?.phone || '',
      dateOfBirth: user?.user_metadata?.date_of_birth || '',
      address: user?.user_metadata?.address || '',
    });
    
    setLoading(false);
  };

  const handleSignOut = async () => {
    await signOut();
    onSignOut();
    window.location.href = '/';
  };

  const handleGoBack = () => {
    window.location.href = '/';
  };

  const handleProfileUpdate = async () => {
    setSaving(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        data: {
          full_name: profileData.fullName,
          phone: profileData.phone,
          date_of_birth: profileData.dateOfBirth,
          address: profileData.address,
        }
      });
      
      if (error) throw error;
      
      alert('Profile updated successfully!');
      setEditingProfile(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const clearSearchHistory = () => {
    if (window.confirm('Are you sure you want to clear all search history?')) {
      localStorage.removeItem(`searchHistory_${user.id}`);
      setSearchHistory([]);
    }
  };

  const deleteSearchItem = (index) => {
    const updated = searchHistory.filter((_, i) => i !== index);
    setSearchHistory(updated);
    localStorage.setItem(`searchHistory_${user.id}`, JSON.stringify(updated));
  };

  if (!user) {
    return null;
  }

  return (
    <div className="account-page">
      {/* Header - Same as Home Page */}
      <div className="header">
        <div className="header-content">
          <div className="logo" onClick={handleGoBack} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="FlightSearch" className="logo-image" />
          </div>
          <div className="nav">
            <a href="/#how-it-works">How it Works</a>
            <a href="/#features">Features</a>
            <a href="/#pricing">Pricing</a>
          </div>
          <div className="header-actions">
            <button 
              onClick={handleSignOut}
              className="sign-out-btn"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="account-container">
        {/* Profile Header Card */}
        <div className="profile-header-card">
          <div className="profile-header-content">
            {/* Avatar */}
            <div className="profile-avatar">
              {user?.user_metadata?.avatar_url ? (
                <img 
                  src={user.user_metadata.avatar_url} 
                  alt="Profile" 
                  className="avatar-image"
                />
              ) : (
                <User className="avatar-icon" />
              )}
            </div>
            
            {/* User Info */}
            <div className="profile-info">
              <h1 className="profile-name">
                {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'}
              </h1>
              <p className="profile-email">
                <Mail className="inline-icon" />
                {user?.email}
              </p>
              <p className="profile-member-since">
                Member since {new Date(user?.created_at).toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              </p>
            </div>

            {/* Stats */}
            <div className="profile-stats">
              <div className="stat-item">
                <div className="stat-value">{trips.length}</div>
                <div className="stat-label">Trips</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat-item">
                <div className="stat-value">{searchHistory.length}</div>
                <div className="stat-label">Searches</div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="tabs-container">
          <div className="tabs">
            <button
              onClick={() => setActiveTab('trips')}
              className={`tab ${activeTab === 'trips' ? 'active' : ''}`}
            >
              <Plane className="tab-icon" />
              My Trips ({trips.length})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
            >
              <History className="tab-icon" />
              Search History ({searchHistory.length})
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`tab ${activeTab === 'subscription' ? 'active' : ''}`}
            >
              <CreditCard className="tab-icon" />
              Subscription
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`tab ${activeTab === 'preferences' ? 'active' : ''}`}
            >
              <Star className="tab-icon" />
              Preferences
            </button>
            <button
              onClick={() => setActiveTab('profile')}
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
            >
              <Settings className="tab-icon" />
              Profile Settings
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="content-section">
          {/* Trips Tab */}
          {activeTab === 'trips' && (
            <div className="trips-content">
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
              ) : trips.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <Plane />
                  </div>
                  <h3>No trips yet!</h3>
                  <p>Start searching for flights and book your first adventure!</p>
                  <button onClick={handleGoBack} className="btn-primary">
                    Search Flights
                  </button>
                </div>
              ) : (
                <div className="trips-grid">
                  {trips.map((trip) => (
                    <div key={trip.id} className="trip-card">
                      {/* Trip Header */}
                      <div className="trip-header">
                        <div>
                          <h3 className="trip-airline">{trip.airline}</h3>
                          {trip.flight_number && (
                            <span className="trip-flight-number">{trip.flight_number}</span>
                          )}
                          <div className="trip-route">
                            <MapPin className="route-icon" />
                            <span>{trip.origin}</span>
                            <ArrowRight className="route-icon" />
                            <span>{trip.destination}</span>
                          </div>
                        </div>
                        <span className={`trip-status ${trip.status}`}>
                          {trip.status === 'confirmed' && <CheckCircle className="status-icon" />}
                          {trip.status}
                        </span>
                      </div>

                      {/* Trip Details */}
                      <div className="trip-details">
                        <div className="trip-detail-item">
                          <Calendar className="detail-icon" />
                          <div>
                            <div className="detail-label">Departure</div>
                            <div className="detail-value">{trip.departure_date}</div>
                          </div>
                        </div>
                        {trip.return_date && (
                          <div className="trip-detail-item">
                            <Calendar className="detail-icon" />
                            <div>
                              <div className="detail-label">Return</div>
                              <div className="detail-value">{trip.return_date}</div>
                            </div>
                          </div>
                        )}
                        <div className="trip-detail-item">
                          <User className="detail-icon" />
                          <div>
                            <div className="detail-label">Passengers</div>
                            <div className="detail-value">{trip.passengers}</div>
                          </div>
                        </div>
                        <div className="trip-detail-item">
                          <Plane className="detail-icon" />
                          <div>
                            <div className="detail-label">Class</div>
                            <div className="detail-value">{trip.class}</div>
                          </div>
                        </div>
                      </div>

                      {/* Trip Footer */}
                      <div className="trip-footer">
                        <div className="trip-price">
                          <DollarSign className="price-icon" />
                          ${trip.price}
                        </div>
                        <div className="trip-booked-date">
                          <Clock className="clock-icon" />
                          Booked {new Date(trip.booking_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Search History Tab */}
          {activeTab === 'history' && (
            <div className="history-content">
              <div className="history-header">
                <h2>Search History</h2>
                {searchHistory.length > 0 && (
                  <button onClick={clearSearchHistory} className="btn-secondary">
                    <Trash2 className="btn-icon" />
                    Clear All
                  </button>
                )}
              </div>

              {searchHistory.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <History />
                  </div>
                  <h3>No search history</h3>
                  <p>Your flight searches will appear here</p>
                  <button onClick={handleGoBack} className="btn-primary">
                    Search Flights
                  </button>
                </div>
              ) : (
                <div className="history-list">
                  {searchHistory.map((search, index) => (
                    <div key={index} className="history-item">
                      <Search className="history-icon" />
                      <div className="history-details">
                        <div className="history-route">
                          {search.from} → {search.to}
                        </div>
                        <div className="history-info">
                          {search.date} • {search.passengers} passenger{search.passengers > 1 ? 's' : ''} • {search.class}
                        </div>
                        <div className="history-time">
                          {new Date(search.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <button 
                        onClick={() => deleteSearchItem(index)}
                        className="history-delete"
                      >
                        <Trash2 className="delete-icon" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <SubscriptionTab user={user} />
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <PreferencesTab user={user} />
          )}

          {/* Profile Settings Tab */}
          {activeTab === 'profile' && (
            <div className="profile-content">
              <div className="profile-section">
                <div className="section-header">
                  <h2>Profile Information</h2>
                  {!editingProfile ? (
                    <button 
                      onClick={() => setEditingProfile(true)}
                      className="btn-secondary"
                    >
                      <Edit2 className="btn-icon" />
                      Edit Profile
                    </button>
                  ) : (
                    <div className="edit-actions">
                      <button 
                        onClick={() => setEditingProfile(false)}
                        className="btn-secondary"
                        disabled={saving}
                      >
                        <X className="btn-icon" />
                        Cancel
                      </button>
                      <button 
                        onClick={handleProfileUpdate}
                        className="btn-primary"
                        disabled={saving}
                      >
                        <Save className="btn-icon" />
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  )}
                </div>

                <div className="form-grid">
                  <div className="form-group">
                    <label>Full Name</label>
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData({...profileData, fullName: e.target.value})}
                      disabled={!editingProfile}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="input-disabled"
                    />
                    <small>Email cannot be changed</small>
                  </div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={profileData.phone}
                      onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                      disabled={!editingProfile}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>

                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      value={profileData.dateOfBirth}
                      onChange={(e) => setProfileData({...profileData, dateOfBirth: e.target.value})}
                      disabled={!editingProfile}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Address</label>
                    <input
                      type="text"
                      value={profileData.address}
                      onChange={(e) => setProfileData({...profileData, address: e.target.value})}
                      disabled={!editingProfile}
                      placeholder="123 Main St, City, State, ZIP"
                    />
                  </div>
                </div>
              </div>

              {/* Danger Zone */}
              <div className="profile-section danger-zone">
                <h3>Danger Zone</h3>
                <p>Once you delete your account, there is no going back. Please be certain.</p>
                <button className="btn-danger">
                  <Trash2 className="btn-icon" />
                  Delete Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default AccountPage;
